import React, { useCallback, useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';
import { Plus, Trash2, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button, Card, CardBody, Checkbox, Switch } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { FormInput, FormNumberInput, FormCheckbox } from '../base';
import { useGuideBuilderGuide } from '../../contexts/GuideBuilderContext';
import { useUpdateStep } from '../../hooks/useGuides';
import { useLanguage } from '@/contexts/LanguageContext';
import { QuizQuestion, QuestionConfig, QuestionOption, GuideStepType } from '@/services/apis/guides';
import StepEditorWrapper from './StepEditorWrapper';

interface QuestionStepEditorFormData {
  questions: QuizQuestion[];
  questionConfig: QuestionConfig;
}

const QuestionStepEditorForm: React.FC = () => {
  const {
    guide: { id: guideId },
    state: { selectedStep },
  } = useGuideBuilderGuide();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation('admin');
  const updateStep = useUpdateStep(guideId || '');
  const [shouldAutoSave, setShouldAutoSave] = useState(false);

  const defaultConfig: QuestionConfig = {
    minimumScore: 60,
    randomizeOrder: false,
    selectRandomCount: undefined,
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty, isValid },
  } = useForm<QuestionStepEditorFormData>({
    defaultValues: {
      questions: [],
      questionConfig: defaultConfig,
    },
  });

  const {
    fields: questionFields,
    append: appendQuestion,
    remove: removeQuestion,
  } = useFieldArray({
    control,
    name: 'questions',
  });

  // Reset form when selectedStep or language changes
  useEffect(() => {
    if (selectedStep) {
      const content = selectedStep.content as any;
      const questions = content?.questions || [];
      // Get questionConfig from step level (not from content)
      // selectedStep should have questionConfig from the step transformation
      const config = (selectedStep as any).questionConfig || defaultConfig;

      reset({
        questions: questions.map((q: any) => ({
          ...q,
          // Handle legacy format where options might be strings
          options:
            q.options?.map((opt: string | QuestionOption) => (typeof opt === 'string' ? { text: opt } : opt)) || [],
          // Handle legacy format where correctAnswer is a number
          correctAnswers: q.correctAnswers || (q.correctAnswer !== undefined ? [q.correctAnswer] : []),
          allowMultiple: q.allowMultiple || false,
        })),
        questionConfig: config,
      });
      setShouldAutoSave(false);
    }
  }, [selectedStep, currentLanguage, reset]);

  // Watch form values and trigger auto-save when dirty and valid
  const formValues = watch();
  useEffect(() => {
    if (isDirty && isValid) {
      setShouldAutoSave(true);
    }
  }, [formValues, isDirty, isValid]);

  const handleSave = handleSubmit(async (data) => {
    if (!selectedStep?.id) return;

    try {
      // Save questions in content (translation-specific)
      // Save questionConfig at step level (applies to all translations)
      await updateStep.mutateAsync({
        stepId: selectedStep.id,
        data: {
          content: {
            questions: data.questions,
          },
          questionConfig: data.questionConfig,
        },
      });
      // Reset form after successful save to mark as not dirty
      reset(data, { keepValues: true });
    } catch (error) {
      console.error('Error saving question step:', error);
    }
  });

  const handleSavePress = () => {
    handleSave();
  };

  const handleAutoSaveComplete = useCallback(() => {
    setShouldAutoSave(false);
    // Reset form to mark as not dirty after auto-save
    const currentValues = watch();
    reset(currentValues, { keepValues: true });
  }, [watch, reset]);

  const addQuestion = () => {
    const newQuestion: QuizQuestion = {
      id: `question-${Date.now()}`,
      title: '',
      options: [{ text: '' }, { text: '' }],
      correctAnswers: [],
      allowMultiple: false,
      explanation: '',
    };
    appendQuestion(newQuestion);
  };

  if (!selectedStep) {
    return null;
  }

  return (
    <StepEditorWrapper
      title={t('guides.builder.stepEditor.questionStep') || 'Question Step'}
      stepType={GuideStepType.QUESTION}
      onSave={handleSavePress}
      isSaveDisabled={!isDirty || updateStep.isPending}
      isPending={updateStep.isPending}
      shouldAutoSave={shouldAutoSave}
      onAutoSaveComplete={handleAutoSaveComplete}
    >
      <div className="space-y-6">
        {/* Question Configuration */}
        <Card className="p-3">
          <CardBody className="space-y-4">
            <h4 className="font-semibold text-lg">Question Settings</h4>

            <div className="space-y-4">
              <FormNumberInput
                name="questionConfig.minimumScore"
                control={control}
                label="Minimum Score (%)"
                placeholder="60"
                min={0}
                max={100}
                description="Minimum score percentage required to pass (default: 60)"
              />

              <Controller
                name="questionConfig.randomizeOrder"
                control={control}
                render={({ field }) => (
                  <Switch isSelected={field.value || false} onValueChange={field.onChange}>
                    Randomize Question Order
                  </Switch>
                )}
              />

              <FormNumberInput
                name="questionConfig.selectRandomCount"
                control={control}
                label="Select Random Questions (optional)"
                placeholder="Leave empty to use all questions"
                min={1}
                description="If set, only this many questions will be randomly selected from the pool"
              />
            </div>
          </CardBody>
        </Card>

        {/* Questions List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-lg">Questions</h4>
            <Button color="primary" variant="flat" onPress={addQuestion} startContent={<Plus className="w-4 h-4" />}>
              Add Question
            </Button>
          </div>

          {questionFields.length === 0 ? (
            <Card>
              <CardBody className="text-center py-8 text-gray-500">
                No questions yet. Click "Add Question" to get started.
              </CardBody>
            </Card>
          ) : (
            questionFields.map((question, questionIndex) => (
              <QuestionEditor
                key={question.id}
                control={control}
                questionIndex={questionIndex}
                onRemove={() => removeQuestion(questionIndex)}
                setValue={setValue}
              />
            ))
          )}
        </div>
      </div>
    </StepEditorWrapper>
  );
};

interface QuestionEditorProps {
  control: any;
  questionIndex: number;
  onRemove: () => void;
  setValue: any;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({ control, questionIndex, onRemove, setValue }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const {
    fields: optionFields,
    append: appendOption,
    remove: removeOption,
  } = useFieldArray({
    control,
    name: `questions.${questionIndex}.options`,
  });

  const question = useWatch({
    control,
    name: `questions.${questionIndex}`,
  });
  const allowMultiple = question?.allowMultiple || false;
  const correctAnswers = (question?.correctAnswers as number[]) || [];

  // Handle allowMultiple change - if switching to single choice and multiple answers selected, keep only first
  useEffect(() => {
    if (!allowMultiple && correctAnswers.length > 1) {
      setValue(`questions.${questionIndex}.correctAnswers`, [correctAnswers[0]], { shouldDirty: true });
    }
  }, [allowMultiple, correctAnswers, questionIndex, setValue]);

  const addOption = () => {
    appendOption({ text: '' });
  };

  const toggleCorrectAnswer = (optionIndex: number) => {
    const currentAnswers = (correctAnswers as number[]) || [];
    const isCorrect = currentAnswers.includes(optionIndex);
    let newCorrectAnswers: number[];

    if (allowMultiple) {
      if (isCorrect) {
        newCorrectAnswers = currentAnswers.filter((ans: number) => ans !== optionIndex);
      } else {
        newCorrectAnswers = [...currentAnswers, optionIndex];
      }
    } else {
      // Single choice - replace the array
      newCorrectAnswers = isCorrect ? [] : [optionIndex];
    }

    setValue(`questions.${questionIndex}.correctAnswers`, newCorrectAnswers, { shouldDirty: true });
  };

  const handleRemoveOption = (optionIndex: number) => {
    if (optionFields.length > 2) {
      // Update correct answers before removing
      const newCorrectAnswers = (correctAnswers as number[])
        .filter((ans: number) => ans !== optionIndex)
        .map((ans: number) => (ans > optionIndex ? ans - 1 : ans));

      setValue(`questions.${questionIndex}.correctAnswers`, newCorrectAnswers, { shouldDirty: true });

      removeOption(optionIndex);
    }
  };

  return (
    <Card className="p-3">
      <CardBody className="space-y-4 overflow-hidden">
        {/* Question Header - Always Visible */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            <Button isIconOnly variant="light" size="sm" onPress={() => setIsExpanded(!isExpanded)} className="min-w-0">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </Button>
            <div className="flex-1">
              <FormInput
                name={`questions.${questionIndex}.title`}
                control={control}
                label={`Question ${questionIndex + 1}`}
                placeholder="Enter question text"
                className="flex-1"
              />
            </div>
          </div>
          <Button isIconOnly variant="light" color="danger" size="sm" onPress={onRemove}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        {/* Collapsible Content */}
        {isExpanded && (
          <div className="space-y-4 pl-8">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Options</label>
                <FormCheckbox
                  name={`questions.${questionIndex}.allowMultiple`}
                  control={control}
                  label="Allow Multiple Answers"
                />
              </div>

              {optionFields.map((option, optionIndex) => {
                const isCorrect = correctAnswers.includes(optionIndex);
                const isDisabled = !allowMultiple && correctAnswers.length > 0 && !isCorrect;

                return (
                  <div key={option.id} className="flex items-center gap-2">
                    <Checkbox
                      isSelected={isCorrect}
                      onValueChange={() => toggleCorrectAnswer(optionIndex)}
                      isDisabled={isDisabled}
                    />
                    <FormInput
                      name={`questions.${questionIndex}.options.${optionIndex}.text`}
                      control={control}
                      placeholder={`Option ${optionIndex + 1}`}
                      className="flex-1"
                    />
                    {optionFields.length > 2 && (
                      <Button
                        isIconOnly
                        variant="light"
                        color="danger"
                        size="sm"
                        onPress={() => handleRemoveOption(optionIndex)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              })}

              <Button variant="flat" size="sm" onPress={addOption} startContent={<Plus className="w-4 h-4" />}>
                Add Option
              </Button>
            </div>

            <FormInput
              name={`questions.${questionIndex}.explanation`}
              control={control}
              label="Explanation (optional)"
              placeholder="Explanation shown after answering"
            />
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default QuestionStepEditorForm;
